import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, formatINR } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Wallet, Clock, Plus, X, PackageX } from "lucide-react";
import { cn } from "@/lib/utils";

const MODES = ["Cash", "UPI", "Card", "Cheque"];
const REASONS = ["Damage", "Item Mismatch", "Expired", "Other"];

export default function CompleteDeliveryModal({ delivery, open, onClose }) {
  const qc = useQueryClient();
  const [outcome, setOutcome] = useState("paid");
  const [mode, setMode] = useState("Cash");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [bank, setBank] = useState("");
  const [card4, setCard4] = useState("");
  const [memo, setMemo] = useState("");
  const [returns, setReturns] = useState([]);

  useEffect(() => {
    if (delivery) {
      setOutcome("paid"); setMode("Cash"); setReference(""); setBank(""); setCard4(""); setMemo(""); setReturns([]);
      setAmount(String(delivery.outstanding || ""));
    }
  }, [delivery]);

  const deductTotal = returns.filter((r) => r.action === "deduct").reduce((s, r) => s + (parseFloat(r.value) || 0), 0);
  const sendBackTotal = returns.filter((r) => r.action === "send_back").reduce((s, r) => s + (parseFloat(r.value) || 0), 0);
  const hasSendBack = returns.some((r) => r.action === "send_back");
  const netOutstanding = Math.max((delivery?.outstanding || 0) - deductTotal - sendBackTotal, 0);

  const complete = useMutation({
    mutationFn: () =>
      api.post(`/deliveries/${delivery.id}/complete`, {
        memo,
        outcome,
        mode: outcome === "paid" ? mode : null,
        amount: outcome === "paid" ? Math.min(parseFloat(amount) || 0, netOutstanding) : 0,
        reference, bank, card_last4: card4,
        returns: returns.map((r) => ({ reason: r.reason, description: r.description, value: parseFloat(r.value) || 0, action: r.action })),
      }),
    onSuccess: (res) => {
      const incomplete = res.data?.status === "Incomplete";
      toast.success(incomplete ? "Marked Incomplete — send returned stock back, then complete" : "Delivery completed");
      qc.invalidateQueries({ queryKey: ["deliveries"] });
      qc.invalidateQueries({ queryKey: ["delivery-detail", delivery.id] });
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onClose();
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!delivery) return null;

  const addReturn = () => setReturns([...returns, { reason: "Damage", description: "", value: "", action: "deduct" }]);
  const updReturn = (i, k, v) => setReturns(returns.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const badReturn = returns.some((r) => !r.reason || !(parseFloat(r.value) > 0));
  const invalid = badReturn || (outcome === "paid" && amount !== "" && parseFloat(amount) < 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="complete-delivery-dialog">
        <DialogHeader>
          <DialogTitle>Complete Delivery</DialogTitle>
          <DialogDescription>Record any returned/mismatched stock, then capture payment or mark as credit.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border bg-secondary/50 p-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Customer</span><span className="font-medium">{delivery.customer_name}</span></div>
            <div className="mt-1 flex justify-between"><span className="text-muted-foreground">Bill total</span><span className="font-mono">{formatINR(delivery.total_amount)}</span></div>
            {(deductTotal + sendBackTotal) > 0 && (
              <div className="mt-1 flex justify-between"><span className="text-muted-foreground">Less returns</span><span className="font-mono text-rose-600 dark:text-rose-400">− {formatINR(deductTotal + sendBackTotal)}</span></div>
            )}
            <div className="mt-1 flex justify-between border-t pt-1"><span className="text-muted-foreground">Net outstanding</span><span className="font-mono font-semibold">{formatINR(netOutstanding)}</span></div>
          </div>

          {/* Returns */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><PackageX className="h-4 w-4" /> Return / Mismatch stock</Label>
              <Button variant="outline" size="sm" onClick={addReturn} data-testid="add-return-button"><Plus className="mr-1 h-3 w-3" /> Add return</Button>
            </div>
            {returns.map((r, i) => (
              <div key={i} className="space-y-2 rounded-lg border bg-card p-3" data-testid={`return-row-${i}`}>
                <div className="flex gap-2">
                  <Select value={r.reason} onValueChange={(v) => updReturn(i, "reason", v)}>
                    <SelectTrigger className="flex-1" data-testid={`return-reason-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{REASONS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input className="w-28 font-mono" type="number" placeholder="Value ₹" value={r.value} onChange={(e) => updReturn(i, "value", e.target.value)} data-testid={`return-value-${i}`} />
                  <button className="text-muted-foreground hover:text-destructive" onClick={() => setReturns(returns.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></button>
                </div>
                <Input placeholder="Description (e.g. 2 packs torn)" value={r.description} onChange={(e) => updReturn(i, "description", e.target.value)} data-testid={`return-desc-${i}`} />
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" onClick={() => updReturn(i, "action", "deduct")} data-testid={`return-deduct-${i}`}
                    className={cn("rounded-md border px-2 py-1.5 text-xs font-medium", r.action === "deduct" ? "border-primary bg-primary text-primary-foreground" : "bg-card hover:bg-secondary")}>
                    Deduct value from bill
                  </button>
                  <button type="button" onClick={() => updReturn(i, "action", "send_back")} data-testid={`return-sendback-${i}`}
                    className={cn("rounded-md border px-2 py-1.5 text-xs font-medium", r.action === "send_back" ? "border-orange-500 bg-orange-500 text-white" : "bg-card hover:bg-secondary")}>
                    Send stock back
                  </button>
                </div>
              </div>
            ))}
            {hasSendBack && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-300">
                Items marked "Send stock back" — this delivery will be saved as <b>Incomplete</b> until the stock is sent back.
              </div>
            )}
          </div>

          {!hasSendBack && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setOutcome("paid")} data-testid="outcome-paid"
                  className={cn("flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium", outcome === "paid" ? "border-primary bg-primary text-primary-foreground" : "bg-card hover:bg-secondary")}>
                  <Wallet className="h-4 w-4" /> Payment collected
                </button>
                <button type="button" onClick={() => setOutcome("credit")} data-testid="outcome-credit"
                  className={cn("flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium", outcome === "credit" ? "border-rose-500 bg-rose-500 text-white" : "bg-card hover:bg-secondary")}>
                  <Clock className="h-4 w-4" /> Pay later (Credit)
                </button>
              </div>

              {outcome === "paid" && (
                <>
                  <div className="space-y-2">
                    <Label>Payment Mode</Label>
                    <Select value={mode} onValueChange={setMode}>
                      <SelectTrigger data-testid="complete-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Amount (₹) — partial allowed</Label>
                    <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="complete-amount-input" />
                    <button className="text-xs text-primary hover:underline" onClick={() => setAmount(String(netOutstanding))}>Fill net outstanding ({formatINR(netOutstanding)})</button>
                  </div>
                  {mode === "UPI" && <div className="space-y-2"><Label>UPI Reference</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="complete-upi-ref" /></div>}
                  {mode === "Card" && <div className="space-y-2"><Label>Card Last 4</Label><Input maxLength={4} value={card4} onChange={(e) => setCard4(e.target.value)} data-testid="complete-card4" /></div>}
                  {mode === "Cheque" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>Cheque No.</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="complete-cheque-no" /></div>
                      <div className="space-y-2"><Label>Bank</Label><Input value={bank} onChange={(e) => setBank(e.target.value)} data-testid="complete-cheque-bank" /></div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label>Memo / Notes</Label>
            <Textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Any specific note about this delivery…" data-testid="complete-memo-input" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => complete.mutate()} disabled={invalid || complete.isPending} data-testid="complete-delivery-confirm">
            <CheckCircle2 className="mr-2 h-4 w-4" /> {hasSendBack ? "Save as Incomplete" : "Mark Completed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
